import { ReactElement, useState } from 'react';
import { HiOutlineDevicePhoneMobile, HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { cn } from '../../../../utils/cn';
import { Episode } from '../../../../api/episodes';
import {
  addMissingRun,
  EpisodeScan,
  ingestEpisodeDb,
  RecoveryResult,
  reimportRun,
  scanSessionEpisodes,
  SessionEpisodeScan,
} from '../../../../api/episodeDbScan';
import {
  useSessionEpisodes,
  useSetEpisodeDbUpload,
  useSetEpisodeDbUploadBatch,
} from '../../../../hooks/useEpisodes';
import OptionSection from './OptionSection';
import GroupHeader from './GroupHeader';
import { configShortLabel } from '../../../../utils/configRole';

/**
 * "Nimbus Recordings" section inside the session Options block: one row
 * per episode bound to this session, with the on-demand full-.db upload
 * request control. Requesting flags the episode server-side; the phone
 * checks at next app start and uploads (prompting the user first when
 * the file is large). Silent when the session has no episodes.
 */

const STATE_CHIPS: Record<string, { label: string; className: string }> = {
  requested: { label: 'upload requested', className: 'cr-bg-amber-100 cr-text-amber-800' },
  fulfilled: { label: 'uploaded', className: 'cr-bg-green-100 cr-text-green-800' },
  denied: { label: 'declined on phone', className: 'cr-bg-red-100 cr-text-red-700' },
};

function whenLabel(ep: Episode): string {
  const ts = ep.db_upload_state === 'requested' ? ep.db_upload_requested_at : ep.db_upload_resolved_at;
  return ts ? new Date(ts).toLocaleString() : '';
}

function EpisodeRow({ ep, sessionId }: { ep: Episode; sessionId?: string }): ReactElement {
  const setUpload = useSetEpisodeDbUpload(sessionId);
  const state = ep.db_upload_state ?? null;
  const chip = state ? STATE_CHIPS[state] : null;
  const requesting = setUpload.isPending;

  const change = (next: 'requested' | null) => {
    setUpload.mutate(
      { episodeId: ep.id, state: next },
      {
        onSuccess: () => toast.success(next === 'requested' ? 'Upload requested' : 'Request cancelled'),
        onError: (err) => toast.error(`Failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-border-t cr-border-gray-100 cr-py-2">
      <span
        className="cr-flex cr-items-center cr-gap-1 cr-text-sm cr-font-medium cr-text-gray-700"
        title={`Device ${ep.device_id || 'unknown'} — episode ${ep.id}`}
      >
        <HiOutlineDevicePhoneMobile className="cr-h-4 cr-w-4" />
        {ep.device_id ? ep.device_id.slice(-4) : '????'}
      </span>
      <span className="cr-text-xs cr-text-gray-500">
        {new Date(ep.start_ts_ms).toLocaleString()}
      </span>
      <span className="cr-text-xs cr-text-gray-400">({ep.live_state})</span>
      {chip && (
        <span
          className={cn('cr-rounded-full cr-px-2 cr-py-0.5 cr-text-xs cr-font-semibold', chip.className)}
          title={whenLabel(ep)}
        >
          {chip.label}
        </span>
      )}
      <span className="cr-grow" />
      {state === 'requested' ? (
        <button
          onClick={() => change(null)}
          disabled={requesting}
          className="cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
            cr-font-medium cr-text-gray-700 hover:cr-bg-gray-50 disabled:cr-opacity-50"
        >
          Cancel request
        </button>
      ) : (
        <button
          onClick={() => change('requested')}
          disabled={requesting || !ep.device_id}
          title={ep.device_id
            ? 'Ask the phone to upload its full recording file at next app start'
            : 'No device id recorded for this episode'}
          className="cr-rounded-md cr-border cr-border-blue-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
            cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-opacity-50"
        >
          {state === 'fulfilled' ? 'Request again' : 'Request recording'}
        </button>
      )}
    </div>
  );
}

const TABLE_LABELS: Record<string, string> = {
  ant_packets: 'ANT packets',
  internal_sensors: 'Internal sensors',
  ble_frames: 'BLE frames',
};

const CHANNEL_LABELS: Record<string, string> = {
  wheel_speed: 'wheel',
  dynamic_pressure: 'dp',
  power: 'power',
  gps: 'gps',
};

function tsRange(startMs: number, endMs: number): string {
  return `${new Date(startMs).toLocaleTimeString()} – ${new Date(endMs).toLocaleTimeString()}`;
}

function backfilledTotal(result: RecoveryResult): number {
  return Object.values(result.backfilled ?? {}).reduce((a, b) => a + b, 0);
}

interface ConfigOption {
  id: string;
  label: string;
}

/** "A — Baseline" style label; special groups show their role marker (WARM/DISC). */
function configLabel(config: any): string {
  const letters = configShortLabel(config?.letter_number);
  const description = config?.description || 'Config';
  return letters === '--' ? description : `${letters} — ${description}`;
}

const UNATTACHED = '__unattached';

interface EpisodeScanBlockProps {
  scan: EpisodeScan;
  configs: ConfigOption[];
  onRunAdded?: () => void;
}

/** One episode's scan outcome: blob info, run cross-check, missing-run flags. */
function EpisodeScanBlock({ scan, configs, onRunAdded }: EpisodeScanBlockProps): ReactElement {
  // Which candidate row has its config dropdown open, and the choice made.
  const [addTarget, setAddTarget] = useState<number | null>(null);
  const [configChoice, setConfigChoice] = useState('');
  const ingest = useMutation({
    mutationFn: () => ingestEpisodeDb(scan.file?.filename ?? ''),
    onSuccess: () => toast.success('Update dispatched — rescan in a minute to verify'),
    onError: (err) => toast.error(`Update failed: ${(err as Error).message}`),
  });
  const addRun = useMutation({
    mutationFn: ({ startTsMs, endTsMs, configId }: { startTsMs: number; endTsMs: number; configId?: string }) =>
      addMissingRun(scan.episode_id, startTsMs, endTsMs, configId),
    onSuccess: (result, variables) => {
      setAddTarget(null);
      if (result.outcome === 'attached') {
        const label = configs.find((c) => c.id === variables.configId)?.label ?? 'config';
        toast.success(`Already recovered — attached to ${label}`);
        onRunAdded?.();
        return;
      }
      if (result.outcome !== 'created') {
        toast.success('This window was already recovered');
        return;
      }
      if (variables.configId) {
        const label = configs.find((c) => c.id === variables.configId)?.label ?? 'config';
        toast.success(`Run recovered into ${label}`);
        onRunAdded?.();
      } else {
        toast.success('Run recovered — it is unattached; use "Add Unallocated Runs" to place it');
      }
    },
    onError: (err) => toast.error(`Add failed: ${(err as Error).message}`),
  });
  const reimport = useMutation({
    mutationFn: ({ runUuid }: { runUuid: string }) => reimportRun(scan.episode_id, runUuid),
    onSuccess: (result) => {
      const rows = backfilledTotal(result);
      if (result.outcome === 'skipped') {
        toast.success(rows > 0
          ? `${rows.toLocaleString()} rows backfilled — file-mode run, artifact left untouched`
          : 'Nothing to re-import for this run');
      } else {
        toast.success(`Re-imported (${rows.toLocaleString()} rows backfilled) — reprocess runs if needed`);
      }
    },
    onError: (err) => toast.error(`Re-import failed: ${(err as Error).message}`),
  });

  const header = (
    <span className="cr-flex cr-items-center cr-gap-2 cr-text-xs cr-font-semibold cr-text-gray-700">
      <HiOutlineDevicePhoneMobile className="cr-h-4 cr-w-4" />
      {scan.device_id ? scan.device_id.slice(-4) : '????'}
      <span className="cr-font-normal cr-text-gray-500">{new Date(scan.start_ts_ms).toLocaleString()}</span>
      {scan.file && <span className="cr-font-normal cr-text-gray-400">{scan.file.filename}</span>}
    </span>
  );

  if (scan.error) {
    return (
      <div className="cr-border-t cr-border-gray-100 cr-py-2">
        {header}
        <div className="cr-mt-1 cr-text-xs cr-text-red-600">Scan failed: {scan.error}</div>
      </div>
    );
  }
  if (!scan.file) {
    return (
      <div className="cr-border-t cr-border-gray-100 cr-py-2">
        {header}
        <div className="cr-mt-1 cr-text-xs cr-text-gray-500">{scan.reason}</div>
      </div>
    );
  }

  const tables = Object.entries(scan.tables ?? {});
  const runs = scan.runs ?? [];
  const missing = scan.missing_run_candidates ?? [];

  return (
    <div className="cr-border-t cr-border-gray-100 cr-py-2">
      <div className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
        {header}
        <span className="cr-grow" />
        {scan.update_available && (
          <button
            onClick={() => ingest.mutate()}
            disabled={ingest.isPending}
            title="Re-ingest this .db: backfill missing runs and rebuild zips whose data grew"
            className="cr-rounded-md cr-border cr-border-blue-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
              cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-opacity-50"
          >
            Update blob info
          </button>
        )}
      </div>

      {/* Blob info — what the phone .db holds vs what the server received live */}
      <table className="cr-mt-2 cr-text-xs cr-text-gray-600">
        <thead>
          <tr className="cr-text-gray-400">
            <th className="cr-pr-4 cr-text-left cr-font-normal">Table</th>
            <th className="cr-pr-4 cr-text-right cr-font-normal">Phone rows</th>
            <th className="cr-pr-4 cr-text-right cr-font-normal">Server rows</th>
            <th className="cr-text-left cr-font-normal" />
          </tr>
        </thead>
        <tbody>
          {tables.map(([name, info]) => (
            <tr key={name}>
              <td className="cr-pr-4">{TABLE_LABELS[name] ?? name}</td>
              <td className="cr-pr-4 cr-text-right">{info.db_rows.toLocaleString()}</td>
              <td className="cr-pr-4 cr-text-right">{info.server_rows.toLocaleString()}</td>
              <td>
                {info.db_has_more && (
                  <span className="cr-rounded-full cr-bg-amber-100 cr-px-2 cr-py-0.5 cr-font-semibold
                    cr-text-amber-800">
                    server missing data
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Runs found in the .db, cross-checked against server RunData */}
      {runs.length > 0 && (
        <div className="cr-mt-2 cr-flex cr-flex-col cr-gap-1 cr-text-xs">
          {runs.map((run) => (
            <div key={run.run_uuid} className="cr-flex cr-flex-wrap cr-items-center cr-gap-2">
              <span className="cr-text-gray-700">
                {run.run_number != null ? `Run ${run.run_number} — ` : ''}
                {run.description || 'Run'}
              </span>
              {run.start_ts_ms != null && run.end_ts_ms != null && (
                <span className="cr-text-gray-400">{tsRange(run.start_ts_ms, run.end_ts_ms)}</span>
              )}
              {run.partial && <span className="cr-text-amber-600">partial</span>}
              {run.on_server ? (
                <span className="cr-rounded-full cr-bg-green-100 cr-px-2 cr-py-0.5 cr-font-semibold
                  cr-text-green-800">
                  on server
                </span>
              ) : (
                <span className="cr-rounded-full cr-bg-red-100 cr-px-2 cr-py-0.5 cr-font-semibold cr-text-red-700">
                  missing on server
                </span>
              )}
              {run.build_error && (
                <span className="cr-text-red-600" title={run.build_error}>build error</span>
              )}
              {run.on_server && run.in_db !== false && (
                <button
                  onClick={() => reimport.mutate({ runUuid: run.run_uuid })}
                  disabled={reimport.isPending}
                  title="Backfill this run's missing rows from the recording and rebuild + reprocess it"
                  className="cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-0.5
                    cr-font-medium cr-text-gray-600 hover:cr-bg-gray-50 disabled:cr-opacity-50"
                >
                  {reimport.isPending && reimport.variables?.runUuid === run.run_uuid
                    ? 'Re-importing…' : 'Re-import'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Windows where >= 2 of wheel/dp/power/gps move together with no run */}
      {missing.length > 0 ? (
        <div className="cr-mt-2 cr-flex cr-flex-col cr-gap-1 cr-text-xs">
          {missing.map((cand) => (
            <div
              key={cand.start_ts_ms}
              className="cr-flex cr-flex-wrap cr-items-center cr-gap-2 cr-rounded-md cr-bg-amber-50 cr-px-2 cr-py-1"
            >
              <span className="cr-font-semibold cr-text-amber-800">Potential missing run</span>
              <span className="cr-text-amber-800">{tsRange(cand.start_ts_ms, cand.end_ts_ms)}</span>
              <span className="cr-text-amber-700">{Math.round(cand.duration_s / 60)} min</span>
              <span className="cr-text-amber-700">
                {cand.channels.map((ch) => CHANNEL_LABELS[ch] ?? ch).join(' + ')}
              </span>
              <span className="cr-grow" />
              {addTarget === cand.start_ts_ms ? (
                <>
                  <select
                    value={configChoice}
                    onChange={(e) => setConfigChoice(e.target.value)}
                    className="cr-rounded-md cr-border cr-border-amber-400 cr-bg-white cr-px-1 cr-py-0.5
                      cr-text-xs cr-text-amber-900"
                  >
                    <option value="" disabled>Select config…</option>
                    {configs.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                    <option value={UNATTACHED}>Unattached</option>
                  </select>
                  <button
                    onClick={() => addRun.mutate({
                      startTsMs: cand.start_ts_ms,
                      endTsMs: cand.end_ts_ms,
                      configId: configChoice === UNATTACHED ? undefined : configChoice,
                    })}
                    disabled={!configChoice || addRun.isPending}
                    className="cr-rounded-md cr-border cr-border-amber-400 cr-bg-white cr-px-2 cr-py-0.5
                      cr-font-medium cr-text-amber-800 hover:cr-bg-amber-100 disabled:cr-opacity-50"
                  >
                    {addRun.isPending ? 'Adding…' : 'Add'}
                  </button>
                  <button
                    onClick={() => setAddTarget(null)}
                    disabled={addRun.isPending}
                    className="cr-rounded-md cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-0.5
                      cr-font-medium cr-text-gray-600 hover:cr-bg-gray-50 disabled:cr-opacity-50"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setAddTarget(cand.start_ts_ms); setConfigChoice(''); }}
                  disabled={addRun.isPending}
                  title="Extract this window from the recording and add it to this session as a run"
                  className="cr-rounded-md cr-border cr-border-amber-400 cr-bg-white cr-px-2 cr-py-0.5
                    cr-font-medium cr-text-amber-800 hover:cr-bg-amber-100 disabled:cr-opacity-50"
                >
                  Add run
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="cr-mt-2 cr-text-xs cr-text-gray-400">No unaccounted riding windows detected.</div>
      )}
    </div>
  );
}

interface NimbusRecordingsSectionProps {
  sessionId?: string;
  /** Phase-1 session summary — supplies the config list for the Add-run dropdown. */
  session?: any;
  /** Refreshes the session after a run is recovered into one of its configs. */
  invalidateSession?: () => Promise<void>;
}

export default function NimbusRecordingsSection({
  sessionId,
  session,
  invalidateSession,
}: NimbusRecordingsSectionProps): ReactElement | null {
  const { data: episodes } = useSessionEpisodes(sessionId);
  const batchUpload = useSetEpisodeDbUploadBatch(sessionId);
  const [scanResult, setScanResult] = useState<SessionEpisodeScan | null>(null);
  const scan = useMutation({
    mutationFn: () => scanSessionEpisodes(sessionId as string),
    onSuccess: setScanResult,
    onError: (err) => toast.error(`Scan failed: ${(err as Error).message}`),
  });

  if (!episodes?.length) return null;
  const openRequests = episodes.filter((ep) => ep.db_upload_state === 'requested').length;
  const requestable = episodes.filter((ep) => ep.device_id && !ep.db_upload_state);
  const configOptions: ConfigOption[] = (session?.configs ?? [])
    .filter((c: any) => c && !c.isDeleted)
    .map((c: any) => ({ id: String(c.id), label: configLabel(c) }));

  const requestAll = () => {
    const asked = requestable.length;
    batchUpload.mutate(
      { episodeIds: requestable.map((ep) => ep.id), state: 'requested' },
      {
        // Count the episodes the server says it wrote, not the ones we sent:
        // the batch PATCH skips a body it cannot apply and still answers 200,
        // so onError never fires for the common partial failure.
        onSuccess: (updated) => {
          const done = updated.length;
          if (done < asked) {
            toast.error(`Requested ${done} of ${asked} recordings — ${asked - done} could not be flagged`);
          } else {
            toast.success(`Requested ${done} recording${done === 1 ? '' : 's'}`);
          }
        },
        onError: (err) => toast.error(`Failed: ${(err as Error).message}`),
      },
    );
  };

  const status = (
    <span className="cr-text-xs cr-font-normal cr-text-gray-500">
      {episodes.length} episode{episodes.length === 1 ? '' : 's'}
      {openRequests > 0 && ` · ${openRequests} upload request${openRequests === 1 ? '' : 's'} open`}
    </span>
  );

  return (
    <>
      <GroupHeader label="Nimbus" />
      <OptionSection title="Recordings" status={status}>
        {requestable.length > 1 && (
          <div className="cr-flex cr-justify-end cr-pb-1">
            <button
              onClick={requestAll}
              disabled={batchUpload.isPending}
              className="cr-rounded-md cr-border cr-border-blue-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs
                cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-opacity-50"
            >
              Request all ({requestable.length})
            </button>
          </div>
        )}
        {episodes.map((ep) => (
          <EpisodeRow key={ep.id} ep={ep} sessionId={sessionId} />
        ))}

        {/* Scan the uploaded .db files: blob info vs server, run
            cross-check, and potential missing-run detection. */}
        <div className="cr-mt-3 cr-flex cr-items-center cr-gap-2 cr-border-t cr-border-gray-200 cr-pt-2">
          <button
            onClick={() => scan.mutate()}
            disabled={scan.isPending}
            title="Open each uploaded recording file and compare it against the server's data"
            className="cr-flex cr-items-center cr-gap-1 cr-rounded-md cr-border cr-border-blue-300 cr-bg-white
              cr-px-2 cr-py-1 cr-text-xs cr-font-medium cr-text-blue-700 hover:cr-bg-blue-50 disabled:cr-opacity-50"
          >
            <HiOutlineMagnifyingGlass className="cr-h-4 cr-w-4" />
            {scan.isPending ? 'Scanning…' : 'Scan recordings'}
          </button>
          {scan.isPending && (
            <span className="cr-text-xs cr-text-gray-400">Downloading and reading recording files…</span>
          )}
        </div>
        {scanResult && !scan.isPending && (
          scanResult.episodes.length > 0 ? (
            <div className="cr-mt-2">
              {scanResult.episodes.map((epScan) => (
                <EpisodeScanBlock
                  key={epScan.episode_id}
                  scan={epScan}
                  configs={configOptions}
                  onRunAdded={() => invalidateSession?.()}
                />
              ))}
            </div>
          ) : (
            <div className="cr-mt-2 cr-text-xs cr-text-gray-400">No episodes found for this session.</div>
          )
        )}
      </OptionSection>
    </>
  );
}
